import React from "react";
import { Link, LinkProps } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { PiLinkSimpleHorizontalBreak } from "react-icons/pi";
import { FiArrowRight } from "react-icons/fi";
import { Tooltip } from "./tooltip";

const linkDivVariants = cva("", {
  variants: {
    variant: {
      // default: "text-[hsl(233,66%,26%)] hover:underline dark:text-azure",
      default: "text-azure hover:underline",
      disabled: "text-disabled",
      clean: "",
      azure: "bg-azure text-azure-foreground shadow-sm hover:bg-azure/80",
      icon: "inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface LinkDivProps
  extends Omit<
      React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >,
      "ref"
    >,
    VariantProps<typeof linkDivVariants> {
  link?: string | LinkProps | any;
  to?: string | LinkProps | any;
  href?: string;
  target?: string;
  tooltip?: string;
  iconSize?: number;
  download?: any;
}

const CombinedLinkIcon = ({ size = 20 }: { size?: number }) => {
  return (
    <div
      className="relative inline-block"
      style={{ width: size, height: size }}
    >
      <PiLinkSimpleHorizontalBreak
        size={size}
        className="absolute inset-0 text-azure"
      />
      <FiArrowRight
        size={size * 0.5}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-azure"
      />
    </div>
  );
};

export const LinkDiv = React.forwardRef<any, LinkDivProps>(
  (
    {
      link: _link,
      to: _to,
      href,
      target = "_blank",
      children,
      variant,
      className,
      tooltip,
      iconSize = 20,
      ...rest
    },
    ref,
  ) => {
    const link = _to ?? _link;

    // Render content based on variant
    const renderContent = () => {
      if (variant === "icon") {
        return <CombinedLinkIcon size={iconSize} />;
      }
      return children;
    };

    // Wrap with tooltip if provided
    const wrapWithTooltip = (element: React.ReactElement) => {
      if (tooltip) {
        return (
          <Tooltip content={tooltip} className="">
            {element}
          </Tooltip>
        );
      }
      return element;
    };

    return link
      ? wrapWithTooltip(
          <Link
            ref={ref}
            {...(typeof link == "string" ? { to: link } : link)}
            className={cn(linkDivVariants({ variant, className }))}
            {...rest}
          >
            {renderContent()}
          </Link>,
        )
      : href
        ? wrapWithTooltip(
            <a
              ref={ref}
              href={href}
              target={target}
              rel="noopener noreferrer"
              className={cn(linkDivVariants({ variant, className }))}
              {...(rest as any)}
            >
              {renderContent()}
            </a>,
          )
        : wrapWithTooltip(
            <div
              ref={ref}
              className={cn(linkDivVariants({ variant, className }))}
              {...rest}
            >
              {renderContent()}
            </div>,
          );
  },
);
